import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

export default function OtherBusinessTypesAccount() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Other Business Types Account"
        description="Flexible business current accounts from MyBank Somalia for partnerships, limited liability companies and other business categories that need tailored banking."
        keywords={['business account Somalia', 'partnership bank account', 'limited liability account', 'MyBank business banking']}
      />
      {/* Hero Section */}
      <div className="relative h-[390px] bg-gradient-to-r from-[#001F5F] to-[#017EFF] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center space-y-12">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-2 text-sm text-white/90">
              <Link to="/" className="hover:text-[#017EFF] transition-colors flex items-center">
                <HomeIcon className="w-4 h-4 mr-1" />
                Home
              </Link>
              <span className="text-white/60 mx-2">/</span>
              <Link to="/#banking-solutions" className="hover:text-[#017EFF] transition-colors">
                Corporate Banking
              </Link>
              <span className="text-white/60 mx-2">/</span>
              <Link to="/services" className="hover:text-[#017EFF] transition-colors">
                Current Accounts
              </Link>
              <span className="text-white/60 mx-2">/</span>
              <span className="text-[#017EFF]">Other Business Types</span>
            </nav>
            {/* Hero Content */}
            <div className="flex flex-col items-center max-w-4xl mx-auto">
              <h1 className="text-5xl font-bold text-white leading-tight text-center mb-6">
                Other Business Types <span className="text-[#017EFF]">Account</span>
              </h1>
              <p className="text-xl text-white/90 text-center leading-relaxed">
                Flexible banking solutions for unique business categories
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 -mt-16 mb-24">
        <div className="bg-white rounded-2xl shadow-xl p-12">
          <div className="grid md:grid-cols-2 gap-20 items-start">
            {/* Left Column */}
            <div className="space-y-10">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold text-[#001F5F]">Custom Current Account</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    For businesses that don’t fit the standard categories, our Other Business Types Account provides customizable features to suit your needs.
                  </p>
                  <p>
                    Enjoy the same robust services—tailored to your unique operations—ensuring smooth financial management and transparency.
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-8">Account Benefits</h3>
                <div className="grid grid-cols-1 gap-6">
                  {[
                    "50‑leaf checkbook",
                    "Custom transaction limits",
                    "Monthly e‑statements",
                    "Batch payment capabilities",
                    "Standing orders setup",
                    "Internet & mobile banking",
                    "SMS & email alerts",
                    "Dedicated relationship manager"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-center text-gray-700">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="pt-4">
                <Link
                  to="/open-account"
                  className="inline-flex items-center px-8 py-4 bg-[#017EFF] text-white font-semibold rounded-lg hover:bg-[#0165CC] transition-all transform hover:scale-105 hover:shadow-lg"
                >
                  Open Account
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* Requirements */}
              <div className="bg-[#001F5F] rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-white mb-8">Requirements</h3>
                <div className="space-y-6">
                  {[
                    "Business registration document",
                    "Proof of address",
                    "Certified ID copies of signatories",
                    "Board resolution or equivalent",
                    "Passport-sized photos",
                    "Tax certificate or equivalent",
                    "Letter of request with signing mandate"
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-start text-white/90">
                      <div className="w-8 h-8 rounded-full bg-[#017EFF]/20 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Services */}
              {/* <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-6">Additional Services</h3>
                <div className="space-y-4">
                  <Link to="/internet-and-mobile-banking" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Internet Banking
                  </Link>
                  <Link to="/other-services" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Batch Payments
                  </Link>
                  <Link to="/other-services" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Payroll Management
                  </Link>
                  <Link to="/swift" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Swift Transfers
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
